import { Capacitor } from "@capacitor/core";
import {
  BranchInitEvent,
  BranchReferringParams,
} from "capacitor-branch-deep-links";
import { analytics } from "lib/analytics";
import { getPathFromReferringParams } from "lib/app/branch";
import { getAppEntryNotificationData } from "lib/app/pushNotifications";
import { PushNotificationData } from "lib/app/types";
import { captureException } from "lib/sentry";
import Router from "next/router";
import { AppOpenEventData, schema } from "shared/analytics";
import wait from "waait";
import { extractSchemePath } from "../parsers";
import { handleOpenInAppBrowser } from "../runtime/handlers";

const APP_ENTRY_PATH_DEFAULT = "/account";

let firstAppLaunch = true;

export type ColdStartHandler = () => boolean | Promise<boolean>;

export const handleSecondLogin: ColdStartHandler = async () => {
  if (!firstAppLaunch) {
    await Router.push(APP_ENTRY_PATH_DEFAULT);
    return true;
  }

  firstAppLaunch = false;
  return false;
};

// If the app was opened by opening a branch deep link, Branch
// will emit an initial event provided you listen early enough.
// This closure listens for that initial notification event and returns an accessor
// NOTE: This is very similar to getAppEntryNotificationData
export const getAppEntryDeepLinkData = (() => {
  let deepLinkData: BranchReferringParams = null;

  if (Capacitor.isPluginAvailable("BranchDeepLinks")) {
    const { BranchDeepLinks } = Capacitor.Plugins;
    const listener = BranchDeepLinks.addListener(
      "init",
      (event: BranchInitEvent) => {
        deepLinkData = event.referringParams;
        // Remove the listener after we receive the initial notification.
        // Future notifications will be handled by useAppUrlOpen
        listener.remove();
      }
    );

    BranchDeepLinks.addListener("initError", (error) => {
      console.error(error);
      captureException(new Error("BranchDeepLinks init error"), {
        extra: { error },
      });
    });

    // Remove the listener if there isn't an initial notification within 5 seconds
    void wait(5000).then(listener.remove);
  }

  return () => deepLinkData;
})();

export const handleEntryNotification: ColdStartHandler = async () => {
  const notificationData: PushNotificationData = getAppEntryNotificationData();
  if (notificationData) {
    console.log("got app entry data from notification:", notificationData);

    analytics.trackEvent<AppOpenEventData>(
      schema.APP_OPENED_BY_PUSH_NOTIFICATION,
      {
        path: notificationData.click_action,
        appColdStart: true,
      }
    );

    if (notificationData.open_in_browser) {
      await handleOpenInAppBrowser(notificationData);
      // Although we've presented the in app browser, the app navigation is still unhandled
      return false;
    } else {
      await Router.push(notificationData.click_action);
      return true;
    }
  }
};

export const handleSchemePath: ColdStartHandler = async () => {
  const response = await Capacitor.Plugins.App.getLaunchUrl();
  const launchUrl = response?.url;

  if (launchUrl) {
    const extractedPath = extractSchemePath(launchUrl);
    if (extractedPath) {
      console.log("got app entry from launch url:", extractedPath);
      analytics.trackEvent<AppOpenEventData>(schema.APP_OPENED_BY_URL, {
        path: extractedPath,
        appColdStart: true,
      });
      await Router.push(extractedPath);
      return true;
    }
  }

  return false;
};

export const handleDeepLink: ColdStartHandler = async () => {
  const deepLinkData = getAppEntryDeepLinkData();
  const path = getPathFromReferringParams(deepLinkData);

  if (path) {
    console.log("got app entry from launch url:", path);
    analytics.trackEvent<AppOpenEventData>(schema.APP_OPENED_BY_URL, {
      path,
      appColdStart: true,
    });
    await Router.push(path);
    return true;
  }

  return false;
};

export const handleDefault: ColdStartHandler = () => {
  return Router.push(APP_ENTRY_PATH_DEFAULT);
};
